var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { redo } from 'prosemirror-history';
import { EditorState, NodeSelection, TextSelection } from 'prosemirror-state';
import { undo } from './basic/keymap/behavior';
import { SHORTCUT_KEY } from './basic/text-shortcut/shortcut-plugin';
import { _delete, appendShadow, checkHasContentBefore, clearFormat, dispatchEvent, formatSelection, getCursorNode, getExistMarks, getExistNodes, getFormat, getShadows, insert, insertShadow, insertText, pasteContent, removeShadow, replace, update, } from './command';
import { EventChannel } from './event';
import { formatGetHTML, IG_TAG, parseHTML } from './formatter';
import { getRealSelectionInfo, warpCommand } from './libs';
var SylApi = /** @class */ (function () {
    function SylApi(configurator, _a) {
        var _this = this;
        var module = _a.module, content = _a.content;
        this.addCommand = function (name, commandObj) {
            if (!name)
                return;
            var adapter = _this;
            if (!_this.command)
                _this.command = {};
            _this.command[name] = Object.keys(commandObj).reduce(function (command, commandName) {
                command[commandName] = warpCommand(adapter, commandObj[commandName]);
                return command;
            }, {});
        };
        this.listenBlur = function () {
            _this.configurator.emit(EventChannel.LocalEvent.ON_BLUR);
        };
        this.listenFocus = function () {
            _this.configurator.emit(EventChannel.LocalEvent.ON_FOCUS);
        };
        this.getSelection = function () {
            var selection = _this.view.state.selection;
            var _a = getRealSelectionInfo(selection), from = _a.from, to = _a.to, anchor = _a.anchor, head = _a.head;
            var selectionInfo = {
                index: from,
                length: to - from,
                anchor: anchor,
                head: head,
            };
            if (selection instanceof NodeSelection)
                selectionInfo.node = selection.node;
            return selectionInfo;
        };
        /**
         * Used to simulate paste behavior to insert content
         */
        this.pasteContent = function (content, option) {
            if (option === void 0) { option = {}; }
            return pasteContent(_this.view, content, option);
        };
        /**
         * Trigger events, which can be used for testing or other purposes.
         */
        this.dispatchEvent = function (event) { return dispatchEvent(_this.view, event); };
        this.configurator = configurator;
        this.root = configurator.mount;
        this.view = configurator.view;
        this.configurator.init(this, module);
        this.domParser = this.configurator.domParser;
        if (content) {
            if (typeof content === 'string')
                this.setHTML(content);
            else
                this.setContent(content);
        }
        this.view.dom.addEventListener('blur', this.listenBlur);
        this.view.dom.addEventListener('focus', this.listenFocus);
        this.emit(EventChannel.LocalEvent.EDITOR_CREATED);
    }
    Object.defineProperty(SylApi.prototype, "length", {
        get: function () {
            return this.view.state.doc.content.size;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "isEmpty", {
        get: function () {
            return this.length === 2;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "text", {
        get: function () {
            return this.getText();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "isFocused", {
        get: function () {
            return this.view.hasFocus();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "undoable", {
        get: function () {
            var state = this.view.state;
            return undo(state);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "redoable", {
        get: function () {
            var state = this.view.state;
            return redo(state);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "editable", {
        get: function () {
            return this.view.editable;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "shortcutable", {
        get: function () {
            // @ts-ignore
            return this.view.state.config.pluginsByKey[SHORTCUT_KEY.key].enable;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SylApi.prototype, "isDestroy", {
        get: function () {
            return !this.view.docView;
        },
        enumerable: false,
        configurable: true
    });
    SylApi.prototype.onError = function (err, args) {
        return this.configurator.onError(err, args);
    };
    // `length` refers to the length of the text
    SylApi.prototype.getText = function (range) {
        try {
            var doc = this.view.state.doc;
            var from_1 = 0;
            var to = doc.nodeSize - 2;
            var length_1;
            if (range) {
                from_1 = range.index;
                to = Math.min(range.index + range.length, to);
                length_1 = range.length;
            }
            var textRes_1 = '';
            doc.nodesBetween(from_1, to, function (node, nodePos) {
                if (node.inlineContent) {
                    var startPos_1 = nodePos + 1;
                    node.content.forEach(function (_node) {
                        if (startPos_1 > from_1 || startPos_1 + _node.nodeSize > from_1) {
                            var text = _node.isText ? _node.textContent : _node.type.spec.getText ? _node.attrs.text : '';
                            if (startPos_1 < from_1)
                                text = text.substr(from_1 - startPos_1);
                            textRes_1 += text;
                        }
                        startPos_1 += _node.nodeSize;
                    });
                }
            });
            return textRes_1.substr(0, length_1);
        }
        catch (e) {
            console.error(e);
            this.onError(e, arguments);
            return '';
        }
    };
    SylApi.prototype.setFormat = function (format, option) {
        if (option === void 0) { option = {}; }
        try {
            var _a = this.view, state_1 = _a.state, dispatch = _a.dispatch;
            var range_1;
            if (option.index !== undefined && option.length !== undefined) {
                range_1 = { from: option.index, to: option.index + option.length };
            }
            var tr_1 = state_1.tr;
            Object.keys(format).map(function (type) {
                tr_1 = formatSelection(type, format[type], range_1)(state_1, tr_1);
            });
            dispatch(tr_1);
            option.focus !== false && this.focus();
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    SylApi.prototype.insert = function (nodeInfo, index) {
        try {
            return insert(this.view, nodeInfo, index);
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    SylApi.prototype.insertText = function (text, format, index) {
        if (format === void 0) { format = {}; }
        try {
            return insertText(this.view, text, format, index);
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    SylApi.prototype.insertCard = function (type, attrs, index) {
        return this.insert({ type: type, attrs: attrs }, index);
    };
    SylApi.prototype.insertInlineCardWithPara = function (type, attrs, index) {
        return this.insert({ type: 'paragraph', content: [{ type: type, attrs: attrs }] }, index);
    };
    SylApi.prototype.replace = function (nodeInfo, replaceOption) {
        try {
            return replace(this.view, nodeInfo, replaceOption);
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    SylApi.prototype.replaceCard = function (type, attrs, index) {
        var config = typeof index === 'number' ? { index: index, length: 1 } : index;
        return this.replace({ type: type, attrs: attrs }, config);
    };
    SylApi.prototype.delete = function (index, length, option) {
        if (option === void 0) { option = {}; }
        try {
            return _delete(this.view, __assign({ index: index, length: length }, option));
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    SylApi.prototype.deleteCard = function (index) {
        this.delete(index, 1);
    };
    SylApi.prototype.on = function (event, handler) {
        this.configurator.on(event, handler);
    };
    SylApi.prototype.off = function (event, handler) {
        this.configurator.off(event, handler);
    };
    SylApi.prototype.emit = function (eventName) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        (_a = this.configurator).emit.apply(_a, __spreadArray([eventName], args, false));
    };
    SylApi.prototype.setSelection = function (config) {
        var index = config.index, _a = config.length, length = _a === void 0 ? 0 : _a, _b = config.scrollIntoView, scrollIntoView = _b === void 0 ? true : _b, anchor = config.anchor, head = config.head, _c = config.selectNode, selectNode = _c === void 0 ? false : _c;
        if (index === undefined && anchor === undefined && head === undefined) {
            throw new TypeError('must provide one of these parameters: [index, anchor, head]');
        }
        var _d = this.view, state = _d.state, dispatch = _d.dispatch;
        var doc = state.doc;
        var max = doc.content.size;
        var resultAnchor = 0;
        var resultHead;
        if (index !== undefined) {
            resultAnchor = index;
            resultHead = Math.min(index + length, max);
        }
        else {
            if (anchor !== undefined)
                resultAnchor = Math.min(anchor, max);
            if (head !== undefined) {
                resultHead = Math.min(head, max);
                if (anchor === undefined)
                    resultAnchor = resultHead;
            }
        }
        var selection = selectNode
            ? NodeSelection.create(doc, resultAnchor)
            : TextSelection.create(doc, resultAnchor, resultHead);
        var tr = state.tr.setSelection(selection);
        dispatch(scrollIntoView === false ? tr : tr.scrollIntoView());
    };
    SylApi.prototype.blur = function () {
        this.view.dom.blur();
    };
    SylApi.prototype.enable = function () {
        this.configurator.setEditable(true);
    };
    SylApi.prototype.disable = function () {
        this.configurator.setEditable(false);
    };
    SylApi.prototype.enableShortcut = function () {
        this.configurator.setShortcutAble(true);
    };
    SylApi.prototype.disableShortcut = function () {
        this.configurator.setShortcutAble(false);
    };
    SylApi.prototype.focus = function () {
        this.view.focus();
    };
    SylApi.prototype.undo = function () {
        var _a = this.view, dispatch = _a.dispatch, state = _a.state;
        var res = undo(state, dispatch);
        // used to update toolbar status
        this.configurator.emit(EventChannel.LocalEvent.SELECTION_CHANGED);
        return res;
    };
    SylApi.prototype.redo = function () {
        var _a = this.view, dispatch = _a.dispatch, state = _a.state;
        var res = redo(state, dispatch);
        // used to update toolbar status
        this.configurator.emit(EventChannel.LocalEvent.SELECTION_CHANGED);
        return res;
    };
    SylApi.prototype.uninstall = function () {
        this.view.dom.removeEventListener('blur', this.listenBlur);
        this.view.dom.removeEventListener('focus', this.listenFocus);
        this.configurator.uninstall();
    };
    SylApi.prototype.getContent = function () {
        return this.view.state.toJSON();
    };
    SylApi.prototype.setContent = function (value) {
        try {
            var state = EditorState.fromJSON(this.view.state, value);
            this.view.updateState(state);
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    /**
     * set editor content by `HTML`
     * `allowUndo` refers to whether to allow undo, the default is false;
     * `silent` refers to whether to emit the `text-change` event, the default is true;
     * `mergeEmpty` refers to whether to merge continuous empty <p> and continuous <br>. The default is true.
     * Note that allowUndo must be false when silent is true.
     */
    SylApi.prototype.setHTML = function (value, options) {
        if (value === void 0) { value = ''; }
        if (options === void 0) { options = {}; }
        try {
            var config = Object.assign({
                allowUndo: false,
                silent: true,
                mergeEmpty: true,
                keepWhiteSpace: undefined,
            }, options);
            var view = this.view;
            var docNode = parseHTML(value, this, config);
            if (config.allowUndo || config.silent === false) {
                view.dispatch(view.state.tr.replace(0, view.state.doc.content.size, docNode.slice(0)));
            }
            else {
                var newState = EditorState.create({
                    schema: view.state.schema,
                    storedMarks: view.state.storedMarks,
                    plugins: view.state.plugins,
                    doc: docNode,
                });
                view.updateState(newState);
            }
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    /**
     * get `HTML content` of editor
     * `mergeEmpty` refers to whether to merge continuous empty <p> and continuous <br>. The default is false.
     * `layerType` refers to the content configured in `ViewMap` or `layers`, the default is ‘template’
     */
    SylApi.prototype.getHTML = function (config) {
        if (config === void 0) { config = { layerType: 'template', mergeEmpty: false }; }
        try {
            if (config.layerType) {
                this.configurator.emit(EventChannel.LocalEvent.SWITCH_LAYER, config.layerType);
            }
            return formatGetHTML(this.view.dom, config, this.configurator.getSylPlugins());
        }
        catch (e) {
            this.onError(e, arguments);
        }
    };
    SylApi.prototype.getExistNodes = function (nodeName) {
        try {
            return getExistNodes(this.view.state.doc, nodeName);
        }
        catch (err) {
            this.onError(err, arguments);
            return [];
        }
    };
    SylApi.prototype.getExistMarks = function (markName) {
        try {
            return getExistMarks(this.view.state, markName);
        }
        catch (err) {
            this.onError(err, arguments);
            return [];
        }
    };
    SylApi.prototype.checkHasContentBefore = function (pos) {
        try {
            return checkHasContentBefore(this.view.state.doc, pos);
        }
        catch (err) {
            this.onError(err, arguments);
            return true;
        }
    };
    SylApi.prototype.update = function (attrs, updateOption) {
        try {
            return update(this.view, attrs, updateOption);
        }
        catch (err) {
            this.onError(err, arguments);
        }
    };
    SylApi.prototype.updateCardAttrs = function (index, attrs, updateOption) {
        if (updateOption === void 0) { updateOption = {}; }
        return this.update(attrs, __assign({ index: index }, updateOption));
    };
    SylApi.prototype.getFormat = function (range) {
        try {
            return getFormat(this.view, range);
        }
        catch (err) {
            this.onError(err);
            return {};
        }
    };
    SylApi.prototype.clearFormat = function () {
        var result = clearFormat(this.view);
        // used to update toolbar status
        this.configurator.emit(EventChannel.LocalEvent.SELECTION_CHANGED);
        return result;
    };
    SylApi.prototype.nodesBetween = function (walker, range) {
        var _a = this.view.state, doc = _a.doc, selection = _a.selection;
        var _b = getRealSelectionInfo(selection), from = _b.from, to = _b.to;
        if (range) {
            from = range.index;
            to = range.index + range.length;
        }
        else if (selection.ranges.length > 1) {
            var ranges = selection.ranges.sort(function (a, b) { return a.$from.pos - b.$from.pos; });
            from = ranges[0].$from.pos;
            to = ranges[ranges.length - 1].$from.pos;
        }
        doc.nodesBetween(from, to, walker);
    };
    SylApi.prototype.getCursorNode = function () {
        return getCursorNode(this.view);
    };
    SylApi.prototype.getShadows = function (key, index, length) {
        var start = index === undefined ? undefined : index;
        var end = start === undefined || length === undefined ? undefined : start + length;
        return getShadows(this.view.state, key, start, end);
    };
    SylApi.prototype.insertShadow = function (data, inline) {
        if (inline === void 0) { inline = false; }
        var _a = this.view, state = _a.state, dispatch = _a.dispatch;
        var index = data.index || data.pos;
        var pos = index === undefined ? this.getSelection().index : index;
        insertShadow(state, dispatch, __assign(__assign({}, data), { pos: pos }), inline);
    };
    SylApi.prototype.removeShadow = function (key) {
        var _a = this.view, state = _a.state, dispatch = _a.dispatch;
        var shadowInfo = removeShadow(state, dispatch, key);
        if (!shadowInfo)
            return;
        var from = shadowInfo.from, to = shadowInfo.to, spec = shadowInfo.spec;
        return { index: from, length: to - from, spec: spec };
    };
    SylApi.prototype.appendShadow = function (data, inline) {
        var _a;
        if (inline === void 0) { inline = false; }
        var _b = this.view, state = _b.state, dispatch = _b.dispatch;
        var shadowData = {
            from: data.index,
            to: data.length + data.index,
            attrs: __assign((_a = {}, _a[IG_TAG] = 'true', _a), data.attrs),
            spec: data.spec,
        };
        appendShadow(state, dispatch, shadowData, inline);
    };
    // used to check whether the `format` is contains by current selection
    SylApi.prototype.isActive = function (type) {
        var format = this.getFormat();
        return !!format[type];
    };
    return SylApi;
}());
export { SylApi };
//# sourceMappingURL=api.js.map